import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Divider,
  Button,
  Typography,
  Box,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import palette from 'src/theme/palette';
import useMediaQuery from '@mui/material/useMediaQuery';

const ContactModal = ({ open, onClose, contact }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  if (!contact) return null;

  // Render property details in modal
  const renderPropertyDetails = () => {
    if (!contact.property_details) {
      return null;
    }

    const property = contact.property_details;

    return (
      <>
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Property Details Section */}
        <Grid item xs={12}>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: palette.text.secondary,
                fontFamily: 'poppinsMedium',
                mb: 2
              }}
            >
              Property Details
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 3,
                alignItems: { xs: 'center', sm: 'flex-start' }
              }}
            >
              {/* Property Image */}
              <Box
                sx={{
                  width: { xs: '100%', sm: 120 },
                  height: 120,
                  borderRadius: 2,
                  overflow: 'hidden',
                  flexShrink: 0,
                  backgroundColor: palette.grey[200]
                }}
              >
                <img
                  src={property.main_image}
                  alt={property.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: palette.grey[300]
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    No Image
                  </Typography>
                </Box>
              </Box>

              {/* Property Info */}
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    mb: 1
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      fontSize: '18px',
                      color: palette.text.primary,
                      lineHeight: 1.2
                    }}
                  >
                    {property?.title}
                  </Typography>

                  {/* Property Link Button */}
                  {property?.property_link && (
                    <Button
                      component="a"
                      href={property?.property_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      sx={{
                        textTransform: 'none',
                        fontFamily: 'poppinsMedium',
                        fontSize: '12px',
                        minWidth: 'auto'
                      }}
                    >
                      View Property
                    </Button>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationIcon sx={{ fontSize: 16, color: palette.text.secondary, mr: 0.5 }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: palette.text.secondary,
                      lineHeight: 1.2
                    }}
                  >
                    {property.address}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: palette.text.disabled,
                      lineHeight: 1.2
                    }}
                  >
                    {property.city || 'N/A'}
                    {property.state && `, ${property.state}`}
                  </Typography>
                </Box>

                {/* Property Stats */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={`$${property.price?.toLocaleString() || 'N/A'}`}
                    variant="outlined"
                    color="primary"
                    size="small"
                  />
                  <Chip label={`${property.bedrooms || 0} Beds`} variant="outlined" size="small" />
                  <Chip
                    label={`${property.bathrooms || 0} Baths`}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={`${property.area_size?.toLocaleString() || 0} sq ft`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: isMobile ? 0 : { xs: 1, sm: 3 },
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          minHeight: isMobile ? '100vh' : '400px',
          maxHeight: isMobile ? '100vh' : '90vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2,
          px: 3,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontFamily: 'poppinsSemiBold',
            color: palette.primary.contrastText
          }}
        >
          Contact Request Details
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* ID */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: palette.text.secondary,
                  fontFamily: 'poppinsMedium',
                  mb: 1
                }}
              >
                Request ID
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular'
                }}
              >
                {contact.id}
              </Typography>
            </Box>
          </Grid>

          {/* Date */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: palette.text.secondary,
                  fontFamily: 'poppinsMedium',
                  mb: 1
                }}
              >
                Submitted Date
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular'
                }}
              >
                {contact.created_at}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Name */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: palette.text.secondary,
                  fontFamily: 'poppinsMedium',
                  mb: 1
                }}
              >
                Full Name
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular',
                  fontSize: '16px'
                }}
              >
                {contact.name}
              </Typography>
            </Box>
          </Grid>

          {/* Email */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: palette.text.secondary,
                  fontFamily: 'poppinsMedium',
                  mb: 1
                }}
              >
                Email Address
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular',
                  fontSize: '16px'
                }}
              >
                {contact.email}
              </Typography>
            </Box>
          </Grid>

          {/* Contact Number */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: palette.text.secondary,
                  fontFamily: 'poppinsMedium',
                  mb: 1
                }}
              >
                Contact Number
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular',
                  fontSize: '16px'
                }}
              >
                {contact.contact_no}
              </Typography>
            </Box>
          </Grid>

          {/* Property Details - Only shows if property_details exists */}
          {renderPropertyDetails()}

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Message - Full message display */}
          <Grid item xs={12}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: palette.text.secondary,
                  fontFamily: 'poppinsMedium',
                  mb: 1
                }}
              >
                Message
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  backgroundColor: palette.grey[50],
                  p: 2,
                  borderRadius: 1,
                  border: `1px solid ${palette.grey[300]}`
                }}
              >
                {contact.message}
              </Typography>
            </Box>
          </Grid>

          {/* Reply*/}
          <Grid item xs={12}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: palette.text.secondary,
                  fontFamily: 'poppinsMedium',
                  mb: 2
                }}
              >
                Replies ({contact.response?.length || 0})
              </Typography>

              {contact.response && contact.response.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {contact.response.map((reply, index) => (
                    <Box
                      key={reply.id}
                      sx={{
                        backgroundColor: palette.grey[50],
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${palette.grey[300]}`,
                        position: 'relative'
                      }}
                    >
                      {/* Reply header with timestamp */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 1
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: palette.text.secondary,
                            fontFamily: 'poppinsMedium',
                            fontSize: '12px'
                          }}
                        >
                          Reply #{index + 1}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: palette.text.disabled,
                            fontFamily: 'poppinsRegular',
                            fontSize: '11px'
                          }}
                        >
                          {new Date(reply.created_at).toLocaleDateString()} at{' '}
                          {new Date(reply.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>

                      {/* Reply message */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: palette.text.primary,
                          fontFamily: 'poppinsRegular',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {reply.response}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: palette.text.disabled,
                    fontFamily: 'poppinsRegular',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    py: 3,
                    backgroundColor: palette.grey[50],
                    borderRadius: 1,
                    border: `1px dashed ${palette.grey[300]}`
                  }}
                >
                  No replies yet
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 100 },
            '&:hover': {
              backgroundColor: palette.primary.dark
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactModal;
